import { generatePath } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { IconStar } from '@components/icons/star';
import { IconStarSolid } from '@components/icons/star-solid';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { JourneyType } from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';

import { useFavoriteItemActions } from '../../favorites/favorites';

type JourneyBreadcrumbProps = {
  journey: JourneyType;
  workspaceId: string;
  onRename?: (title: string) => Promise<void>;
  onIsEditingChange?: (isEditing: boolean) => void;
  showEditSidebar: () => void;
  isBuilder?: boolean;
};

export const JourneyBreadcrumb = ({
  journey,
  workspaceId,
  onRename,
  onIsEditingChange,
  showEditSidebar,
  isBuilder = false,
}: JourneyBreadcrumbProps) => {
  const { t } = useScopedTranslate(locale);

  const { favoriteItem, addToFavorites, removeFromFavorites } = useFavoriteItemActions({
    contentType: 'journey',
    shortId: journey.shortId,
  });

  const handleFavoriteToggle = () => {
    if (favoriteItem) {
      removeFromFavorites.mutate(favoriteItem.shortId);
    } else {
      addToFavorites.mutate({
        contentType: 'journey',
        objectShortId: journey.shortId,
      });
    }
  };

  const items = [
    ...(!isBuilder
      ? [
          { label: journey.workspaceTitle },
          {
            label: t('journeys'),
            link: generatePath(routePaths.workspaceJourneyList, { workspaceId }),
          },
        ]
      : []),
    {
      label: journey.title,
      onRename: onRename,
      onIsEditingChange: onIsEditingChange,
      extraMenuItems: [
        {
          id: 'edit-journey-details',
          label: t('editJourneyDetails'),
          dataCy: 'edit-journey-details',
          icon: <IconEdit />,
          onSelect: showEditSidebar,
        },
      ],
    },
  ];

  return (
    <Stack space={isBuilder ? 0 : 2} orientation="horizontal" align="center">
      <Breadcrumb items={items} />
      <IconButton
        className="favorite-button"
        size="sm"
        variant="ghost-purple"
        icon={favoriteItem ? <IconStarSolid color="orange100" /> : <IconStar />}
        onClick={handleFavoriteToggle}
        data-cy={favoriteItem ? 'remove-from-favorites' : 'add-to-favorites'}
        tooltip={favoriteItem ? t('removeFromFavorites') : t('addToFavorites')}
      />
    </Stack>
  );
};

const locale = {
  en: {
    journeys: 'Journeys',
    addToFavorites: 'Add to favourites',
    removeFromFavorites: 'Remove from favourites',
    editJourneyDetails: 'Edit journey details',
  },
};
